import { useEffect, useState } from "react";
import { Save, Plus, X, RefreshCw } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { Select } from "antd";
import { QUOTATION_TEMPLATE_URL } from "../../constants/api";

export default function QuotationTemplateEditor() {
  const { fetch, update } = useApiMethods();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // null = the default template, used by every project without its own.
  const [projectId, setProjectId] = useState(null);
  const [projects, setProjects] = useState([]);

  const load = async (pid = projectId) => {
    const res = await fetch(QUOTATION_TEMPLATE_URL, pid ? { project_id: pid } : {});
    if (res?.status === "success") {
      setForm(res.data);
      setProjects(res.projects || []);
    }
    else ToastMsg("error", res?.message || "Failed to load");
  };
  useEffect(() => { load(projectId); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addItem = (key, blank) => set(key, [...(form[key] || []), blank]);
  const updItem = (key, i, field, v) => set(key, form[key].map((x, idx) => idx === i ? { ...x, [field]: v } : x));
  const delItem = (key, i) => set(key, form[key].filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    const res = await update(QUOTATION_TEMPLATE_URL, { ...form, project_id: projectId });
    setSaving(false);
    if (res?.status === "success") {
      ToastMsg("success", "Template saved");
      load(projectId);
    }
    else ToastMsg("error", res?.message || "Failed");
  };

  return (
    <ContainerCard>
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Quotation Template</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Static sections shown on every quotation. Each project can have its own; the default covers the rest.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => load(projectId)}><RefreshCw className="h-3.5 w-3.5" /></Button>
            <Button size="sm" onClick={save} disabled={saving}><Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save Template"}</Button>
          </div>
        </div>

        <Section title="Which template">
          <Grid>
            <Field label="Project">
              <Select className="w-full" value={projectId ?? "default"}
                onChange={v => setProjectId(v === "default" ? null : v)}
                options={[{ value: "default", label: "Default (every project without its own)" },
                          ...projects.map(p => ({ value: p.id, label: `${p.name}${p.has_template ? "" : " — using default"}` }))]} />
            </Field>
            <div className="text-[11px] text-gray-500 self-end pb-1">
              {projectId && !form.is_project_template
                ? "This project has no template yet and prints with the default. Saving creates one, starting from the default."
                : projectId ? "Quotations filed under this project print with this template."
                : "Used by every project that has not saved its own."}
            </div>
          </Grid>
        </Section>

        <Section title="Branding">
          <Grid>
            <Field label="Brand name (e.g. Gro Wing CRM, HRZyra, eMedAyush)"><Input value={form.brand_name || ""} onChange={e => set("brand_name", e.target.value)} /></Field>
            <Field label="Accent colour">
              <div className="flex gap-2 items-center">
                <input type="color" value={form.accent_color || "#0f766e"} onChange={e => set("accent_color", e.target.value)} className="h-9 w-12 rounded border border-gray-300" />
                <Input value={form.accent_color || ""} onChange={e => set("accent_color", e.target.value)} />
              </div>
            </Field>
            <Field label="Footer phone"><Input value={form.footer_phone || ""} onChange={e => set("footer_phone", e.target.value)} /></Field>
            <Field label="Footer email"><Input value={form.footer_email || ""} onChange={e => set("footer_email", e.target.value)} /></Field>
            <Field label="Footer website"><Input value={form.footer_website || ""} onChange={e => set("footer_website", e.target.value)} /></Field>
            <Field label="Module catalogue page">
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 h-9">
                <input type="checkbox" checked={form.show_products_page !== false} onChange={e => set("show_products_page", e.target.checked)} />
                Show the "Products of …" page (eMedHub modules)
              </label>
            </Field>
            <Field label="New plans are priced">
              <Select className="w-full" value={form.default_unit_label || ""}
                onChange={v => set("default_unit_label", v)}
                options={[{ value: "", label: "By the CMS / HIMS tier calculator" },
                          { value: "User", label: "Per user (CRM)" },
                          { value: "Employee", label: "Per employee (HRMS)" },
                          { value: "Bed", label: "Per bed (hospital)" }]} />
            </Field>
            {form.default_unit_label && (
              <Field label="Per-unit billing">
                <Select className="w-full" value={Number(form.default_billing_months) === 1 ? 1 : 12}
                  onChange={v => set("default_billing_months", v)}
                  options={[{ value: 12, label: "Monthly rate × 12 (per year)" },
                            { value: 1, label: "One-time / annual price" }]} />
              </Field>
            )}
          </Grid>
        </Section>

        <Section title="Cover">
          <Grid>
            <Field label="Cover Title"><Input value={form.cover_title} onChange={e => set("cover_title", e.target.value)} /></Field>
            <Field label="Cover Subtitle"><Input value={form.cover_subtitle} onChange={e => set("cover_subtitle", e.target.value)} /></Field>
          </Grid>
        </Section>

        <Section title="Introduction">
          <Field label="Intro Text"><Textarea rows={6} value={form.intro_text} onChange={e => set("intro_text", e.target.value)} /></Field>
          <Grid>
            <Field label="Founder Name / Title"><Input value={form.founder_name} onChange={e => set("founder_name", e.target.value)} /></Field>
            <Field label="Founder Org"><Input value={form.founder_org} onChange={e => set("founder_org", e.target.value)} /></Field>
          </Grid>
        </Section>

        <Section title="Why Choose Us (6 cards)">
          <ListEditor items={form.why_choose_us} onAdd={() => addItem("why_choose_us", { title: "", description: "" })}
            onDel={(i) => delItem("why_choose_us", i)}
            render={(it, i) => (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Title" value={it.title} onChange={e => updItem("why_choose_us", i, "title", e.target.value)} />
                <Input className="md:col-span-2" placeholder="Description" value={it.description} onChange={e => updItem("why_choose_us", i, "description", e.target.value)} />
              </div>
            )} />
        </Section>

        <Section title="Certifications">
          <ListEditor items={form.certifications} onAdd={() => addItem("certifications", { name: "", description: "" })}
            onDel={(i) => delItem("certifications", i)}
            render={(it, i) => (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Name" value={it.name} onChange={e => updItem("certifications", i, "name", e.target.value)} />
                <Input className="md:col-span-2" placeholder="Description" value={it.description} onChange={e => updItem("certifications", i, "description", e.target.value)} />
              </div>
            )} />
        </Section>

        <Section title="What We Do (cards)">
          <ListEditor items={form.what_we_do} onAdd={() => addItem("what_we_do", { title: "", description: "" })}
            onDel={(i) => delItem("what_we_do", i)}
            render={(it, i) => (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Title" value={it.title} onChange={e => updItem("what_we_do", i, "title", e.target.value)} />
                <Input className="md:col-span-2" placeholder="Description" value={it.description} onChange={e => updItem("what_we_do", i, "description", e.target.value)} />
              </div>
            )} />
        </Section>

        <Section title="Key Features (bullet list)">
          <ListEditor items={(form.key_features || []).map(k => ({ value: k }))}
            onAdd={() => set("key_features", [...(form.key_features || []), ""])}
            onDel={(i) => set("key_features", form.key_features.filter((_, idx) => idx !== i))}
            render={(it, i) => (
              <Input value={form.key_features[i] || ""} onChange={e => {
                const copy = [...form.key_features]; copy[i] = e.target.value; set("key_features", copy);
              }} />
            )} />
        </Section>

        <Section title="Text Sections">
          <Field label="Terms & Conditions"><Textarea rows={5} value={form.terms_text} onChange={e => set("terms_text", e.target.value)} /></Field>
          <Field label="Implementation & Support"><Textarea rows={8} value={form.implementation_text} onChange={e => set("implementation_text", e.target.value)} /></Field>
          <Field label="AMC Coverage"><Textarea rows={5} value={form.amc_text} onChange={e => set("amc_text", e.target.value)} /></Field>
          <Field label="Software Upgrades"><Textarea rows={4} value={form.upgrades_text} onChange={e => set("upgrades_text", e.target.value)} /></Field>
          <Field label='"Other Charges" text (shown below pricing)'><Textarea rows={8} value={form.other_charges_text} onChange={e => set("other_charges_text", e.target.value)} /></Field>
        </Section>

        <Section title="Payment Details">
          <Grid>
            <Field label="Account Holder Name"><Input value={form.bank_holder_name} onChange={e => set("bank_holder_name", e.target.value)} /></Field>
            <Field label="Bank Name"><Input value={form.bank_name} onChange={e => set("bank_name", e.target.value)} /></Field>
            <Field label="Account No."><Input value={form.bank_account_no} onChange={e => set("bank_account_no", e.target.value)} /></Field>
            <Field label="Branch"><Input value={form.bank_branch} onChange={e => set("bank_branch", e.target.value)} /></Field>
            <Field label="IFSC"><Input value={form.bank_ifsc} onChange={e => set("bank_ifsc", e.target.value)} /></Field>
            <Field label="UPI ID"><Input value={form.upi_id} onChange={e => set("upi_id", e.target.value)} /></Field>
            <Field label="UPI Payee Name"><Input value={form.upi_payee_name} onChange={e => set("upi_payee_name", e.target.value)} /></Field>
          </Grid>
        </Section>

        <Section title="Contact / Footer">
          <Grid>
            <Field label="Company Name"><Input value={form.company_name} onChange={e => set("company_name", e.target.value)} /></Field>
            <Field label="Website"><Input value={form.company_website} onChange={e => set("company_website", e.target.value)} /></Field>
            <Field label="Emails (comma-separated)"><Input value={form.company_emails} onChange={e => set("company_emails", e.target.value)} /></Field>
            <Field label="Phones (comma-separated)"><Input value={form.company_phones} onChange={e => set("company_phones", e.target.value)} /></Field>
            <Field label="Address"><Input value={form.company_address} onChange={e => set("company_address", e.target.value)} /></Field>
          </Grid>
        </Section>

        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={saving}><Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save Template"}</Button>
        </div>
      </div>
    </ContainerCard>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}
function ListEditor({ items = [], onAdd, onDel, render }) {
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex-1">{render(it, i)}</div>
          <button onClick={() => onDel(i)} className="text-red-500 p-1 mt-1"><X className="h-4 w-4" /></button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={onAdd}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
    </div>
  );
}
